import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIonToast } from "@ionic/react";
import debounce from "debounce-promise";
import { produce } from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import CircleButton from "@components/buttons/CircleButton";
import ConditionalTooltip from "@components/ConditionalTooltip";
import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import CareProfileDateInput from "@components/fields/CareProfileDateInput";
import CareProfileInput from "@components/fields/CareProfileInput";
import DataSelect from "@components/fields/DataSelect";
import LocationAutocompleteInput from "@components/fields/LocationAutocompleteInput";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import Form from "@components/forms/Form";
import ButtonLink from "@components/typography/ButtonLink";
import useCopyToClipboard from "@hooks/useCopyToClipboard";
import {
  CareProfileAcuityEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileImmigrationStatusEnum,
  CareProfileMaritalStatusEnum,
  CareProfileTypesOfSupportNeededEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  CareProfileUseCaseEnum,
  ClientRoleRelationshipEnum,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  PronounsEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { cityOrLowerLocationTypes } from "@utils/locationUtils";
import {
  optionsForBoolean,
  optionsForMaritalStatus,
  optionsForPronouns,
  optionsForTypesOfSupportNeeded
} from "@utils/optionUtils";
import { dateToMDYString } from "@utils/timeUtils";

type FormData = {
  acuity?: CareProfileAcuityEnum | null;
  culturalConsiderations?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  exactAge: number | null;
  guardianMaritalStatus?: CareProfileMaritalStatusEnum | null;
  immigrationStatus?: CareProfileImmigrationStatusEnum | null;
  isDeceased: boolean;
  languageCode?: string | null;
  maritalStatus?: CareProfileMaritalStatusEnum | null;
  name?: string | null;
  placeId?: string | null;
  pregnancyDueDate?: string | null;
  pronouns?: PronounsEnum | null;
  pronounsOther?: string | null;
  typesOfSupportNeeded?: CareProfileTypesOfSupportNeededEnum[];
};

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

const CareProfileDemographicsForm = ({ careProfileFieldRules, journey }: Props) => {
  const careProfile = journey.careProfile;

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [showExactAge, setShowExactAge] = useState<boolean>(!careProfile.dateOfBirth && !!careProfile.exactAge);
  const [presentDeceasedGrievingMismatchToast] = useIonToast();

  let defaultDateOfBirth = null;
  if (careProfile.dateOfBirth) {
    defaultDateOfBirth = dateToMDYString(careProfile.dateOfBirth);
  }

  let defaultDateOfDeath = null;
  if (careProfile.dateOfDeath) {
    defaultDateOfDeath = dateToMDYString(careProfile.dateOfDeath);
  }

  let defaultPregnancyDueDate = null;
  if (careProfile.pregnancyDueDate) {
    defaultPregnancyDueDate = dateToMDYString(careProfile.pregnancyDueDate);
  }

  const defaultValues: FormData = {
    acuity: careProfile.acuity ?? null,
    culturalConsiderations: careProfile.culturalConsiderations ?? null,
    dateOfBirth: defaultDateOfBirth,
    dateOfDeath: defaultDateOfDeath,
    exactAge: careProfile.exactAge ?? null,
    guardianMaritalStatus: careProfile.guardianMaritalStatus ?? null,
    immigrationStatus: careProfile.immigrationStatus ?? null,
    isDeceased: careProfile.isDeceased || false,
    languageCode: careProfile.languageCode ?? null,
    maritalStatus: careProfile.maritalStatus ?? null,
    name: careProfile.name ?? null,
    placeId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS
      ? "ChIJfX6-HwSbf4gR4Aa9pkeWBLY"
      : careProfile.location.placeId,
    pregnancyDueDate: defaultPregnancyDueDate,
    pronouns: careProfile.pronouns,
    pronounsOther: careProfile.pronounsOther,
    typesOfSupportNeeded: careProfile.typesOfSupportNeeded
  };

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      acuity: yup.string().nullable().oneOf(Object.values(CareProfileAcuityEnum)),
      culturalConsiderations: yup.string().nullable(),
      dateOfBirth: yup.string().nullable(),
      dateOfDeath: yup.string().nullable(),
      exactAge: yup.number().defined().nullable(),
      guardianMaritalStatus: yup.string().nullable().oneOf(Object.values(CareProfileMaritalStatusEnum)),
      immigrationStatus: yup.string().nullable().oneOf(Object.values(CareProfileImmigrationStatusEnum)),
      isDeceased: yup.boolean().defined(),
      languageCode: yup.string().nullable(),
      maritalStatus: yup.string().nullable().oneOf(Object.values(CareProfileMaritalStatusEnum)),
      name: yup.string().nullable(),
      placeId: yup.string().nullable(),
      pregnancyDueDate: yup.string().nullable(),
      pronouns: yup.string().nullable().oneOf(Object.values(PronounsEnum)),
      pronounsOther: yup.string().nullable(),
      typesOfSupportNeeded: yup
        .array()
        .of(yup.string().oneOf(Object.values(CareProfileTypesOfSupportNeededEnum)).defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const previousIsDeceasedValue = careProfile.isDeceased;
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...valuesWithoutErrors,
        id: journey.careProfile.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {
          const updatedCareProfile = response.data?.careProfileUpdate?.careProfile;
          if (
            !previousIsDeceasedValue &&
            updatedCareProfile?.isDeceased &&
            updatedCareProfile?.useCase !== CareProfileUseCaseEnum.GRIEVING
          ) {
            presentDeceasedGrievingMismatchToast({
              buttons: [{ role: "cancel", text: intl.formatMessage({ id: "dictionary.ok" }) }],
              color: "warning",
              message: intl.formatMessage({ id: "forms.careProfileNew.deceasedGrievingMismatchWarning" }),
              position: "top"
            });
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [
    careProfile.isDeceased,
    form,
    intl,
    journey.careProfile.id,
    presentDeceasedGrievingMismatchToast,
    updateCareProfile
  ]);

  const notDebouncedFields = useMemo(() => ["acuity", "age"], []);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch((_, { name }) => {
      if (name && notDebouncedFields.includes(name)) {
        form.handleSubmit(submitCareProfile, submitCareProfile)();
      } else {
        form.handleSubmit(debouncedSubmit, submitCareProfile)();
      }
    });
    return () => subscription.unsubscribe();
  }, [form, notDebouncedFields, submitCareProfile]);

  const copyToClipboard = useCopyToClipboard(careProfile.location.address);

  const maritalStatusOptions = optionsForMaritalStatus(intl);
  const pronounsOptions = optionsForPronouns(intl);
  const typesOfSupportNeededOptions = optionsForTypesOfSupportNeeded(intl);
  const booleanOptions = optionsForBoolean(intl);

  const isDeceased = form.watch("isDeceased");

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.MYSELF}
            fieldName={CareProfileFieldNameEnum.NAME}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="name"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.name}
              label={intl.formatMessage({ id: "forms.careProfileNew.nameLabel" })}
              register={form.register("name")}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.MYSELF}
            fieldName={CareProfileFieldNameEnum.PRONOUNS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pronouns"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.pronouns}
              label={intl.formatMessage({ id: "forms.careProfileNew.pronounsLabel" })}
              name="pronouns"
              options={pronounsOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={journey.ownerRole?.relationship?.relationshipOption !== ClientRoleRelationshipEnum.MYSELF}
            fieldName={CareProfileFieldNameEnum.PRONOUNS_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pronounsOther"
            otherField="pronouns"
            otherValue={PronounsEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.pronounsOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.pronounsOtherLabel" })}
              register={form.register("pronounsOther")}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={!showExactAge}
            fieldName={CareProfileFieldNameEnum.DATE_OF_BIRTH}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="dateOfBirth"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileDateInput<FormData>
              control={form.control}
              error={form.formState.errors.dateOfBirth}
              label={intl.formatMessage({ id: "forms.careProfileNew.dateOfBirthLabel" })}
              name="dateOfBirth"
              pastOnly
              register={form.register("dateOfBirth")}
            />
            <ButtonLink onClick={() => setShowExactAge(previousShowExactAge => !previousShowExactAge)}>
              <FormattedMessage id="forms.careProfileNew.showExactAge" />
            </ButtonLink>
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={showExactAge}
            fieldName={CareProfileFieldNameEnum.EXACT_AGE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="exactAge"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.exactAge}
              label={intl.formatMessage({ id: "forms.careProfileNew.exactAgeLabel" })}
              max={125} // the world's oldest person lived to 122
              min={0}
              register={form.register("exactAge")}
              type="number"
            />
            <ButtonLink onClick={() => setShowExactAge(previousShowExactAge => !previousShowExactAge)}>
              <FormattedMessage id="forms.careProfileNew.hideExactAge" />
            </ButtonLink>
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.IS_DECEASED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="isDeceased"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.isDeceased}
              inTracker={false}
              label={intl.formatMessage({ id: "forms.careProfileNew.isDeceasedLabel" })}
              name="isDeceased"
              options={booleanOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={isDeceased}
            fieldName={CareProfileFieldNameEnum.DATE_OF_DEATH}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="dateOfDeath"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileDateInput<FormData>
              control={form.control}
              error={form.formState.errors.dateOfDeath}
              label={intl.formatMessage({ id: "forms.careProfileNew.dateOfDeathLabel" })}
              pastOnly
              register={form.register("dateOfDeath")}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.PREGNANCY_DUE_DATE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pregnancyDueDate"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileDateInput<FormData>
              control={form.control}
              error={form.formState.errors.pregnancyDueDate}
              label={intl.formatMessage({ id: "forms.careProfileNew.pregnancyDueDateLabel" })}
              register={form.register("pregnancyDueDate")}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <div className={styles.selectWithCopyButton}>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.PLACE_ID}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="placeId"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <LocationAutocompleteInput
                allowedTypes={cityOrLowerLocationTypes}
                control={form.control}
                error={form.formState.errors.placeId}
                label={intl.formatMessage({ id: "forms.careProfileNew.placeIdLabel" })}
                location={{ address: careProfile.location.address, placeId: careProfile.location.placeId }}
                name="placeId"
              />
            </CareProfileFormCondition>
            {careProfile.location.address && (
              <div className={styles.copyButton}>
                <ConditionalTooltip
                  condition={copyToClipboard.showTooltip}
                  content={intl.formatMessage({ id: "dictionary.copied" })}
                >
                  {tooltipTriggerProps => (
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.copy"
                      fill="clear"
                      icon="clone"
                      onClick={copyToClipboard.handleCopyToClipboard}
                      {...tooltipTriggerProps}
                    />
                  )}
                </ConditionalTooltip>
              </div>
            )}
          </div>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.LANGUAGE_CODE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="languageCode"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <DataSelect<FormData>
              control={form.control}
              error={form.formState.errors.languageCode}
              hasNoneOption
              label={intl.formatMessage({ id: "forms.careProfileNew.languageCodeLabel" })}
              name="languageCode"
              type="languages"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.MARITAL_STATUS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="maritalStatus"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.maritalStatus}
              label={intl.formatMessage({ id: "forms.careProfileNew.maritalStatusLabel" })}
              name="maritalStatus"
              options={maritalStatusOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.GUARDIAN_MARITAL_STATUS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="guardianMaritalStatus"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.guardianMaritalStatus}
              label={intl.formatMessage({ id: "forms.careProfileNew.guardianMaritalStatusLabel" })}
              name="guardianMaritalStatus"
              options={maritalStatusOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CULTURAL_CONSIDERATIONS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="culturalConsiderations"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.culturalConsiderations}
              label={intl.formatMessage({
                id: "forms.careProfileNew.culturalConsiderationsLabel"
              })}
              register={form.register("culturalConsiderations")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.TYPES_OF_SUPPORT_NEEDED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="typesOfSupportNeeded"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.typesOfSupportNeeded}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.typesOfSupportNeededLabel" })}
              name="typesOfSupportNeeded"
              options={typesOfSupportNeededOptions}
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileDemographicsForm;
